<template>
    <st-modal
        id="language-choice-modal"
        ref="modal"
        size="md"
        is-centered
        hideable
        hide-header-delimiter
        hide-footer-delimiter
        hide-footer
        customHeaderClass="pb-0"
        customBodyClass="pt-3"
        :no-close-on-backdrop="!isSwitch"
        :no-close-on-esc="!isSwitch"
        @modalHidden="onHidden"
    >
        <template #header>
            <p id="language-choice-title" class="font-size-h5 font-weight-bold mb-0 pb-0">{{ title }}</p>
        </template>
        <template #body>
            <p class="text-dark-75 mb-3">{{ description }}</p>
            <div class="d-flex flex-wrap mb-5">
                <span
                    v-for="target in appliesTo"
                    :key="target.key"
                    class="label label-lg label-inline label-primary mr-2 mb-2"
                >
                    <i :class="['fas', target.icon, 'text-white', 'mr-2']"></i>{{ target.label }}
                </span>
            </div>
            <div class="row no-gutters" role="group" aria-labelledby="language-choice-title">
                <div
                    v-for="(option, index) in options"
                    :key="option.language"
                    class="col-6 d-flex mb-5"
                    :class="index === 0 ? 'pr-2' : 'pl-2'"
                >
                    <st-button
                        variant="primary"
                        outline
                        block
                        customClass="d-flex flex-column align-items-start text-left p-3 p-md-5"
                        :spinner="pendingLanguage === option.language"
                        :disabled="isSaving && pendingLanguage !== option.language"
                        :callback="() => choose(option)"
                    >
                        <span class="d-flex align-items-center justify-content-between w-100 mb-4">
                            <span class="label label-inline label-light-primary font-weight-bold h-auto py-1 text-left">{{ option.tag }}</span>
                            <i v-if="option.arrow" class="fas fa-arrow-right" aria-hidden="true"></i>
                        </span>
                        <img :src="option.flag" alt="" class="h-45px w-45px rounded-sm mb-3" />
                        <span class="font-size-h4 font-weight-bolder" :lang="option.language">{{ option.name }}</span>
                        <span class="font-size-sm font-weight-bold mt-1" :lang="option.language">{{ option.caption }}</span>
                        <span class="font-size-sm mt-2" :lang="option.language">{{ option.description }}</span>
                    </st-button>
                </div>
            </div>
            <p class="text-dark-50 font-size-sm mb-0">
                <i class="fas fa-info-circle mr-2"></i>{{ footnote }}
            </p>
        </template>
    </st-modal>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import i18nService from '@/shared/services/i18n.service';
    import Message from '@/shared/message/message';
    import { LANGUAGE_CHOICE_MODE, getLanguageChoiceOptions } from '@/shared/i18n/language-choice';
    import { CLEAR_LANGUAGE_CHOICE, UPDATE_PROFILE_LANGUAGE } from '@/modules/auth/auth-store';

    const CHOICE_KEYS = 'PROFILE.SECTIONS.LANGUAGE.CHOICE';

    const APPLIES_TO = [
        { key: 'PLATFORM', icon: 'fa-desktop' },
        { key: 'EMAIL', icon: 'fa-envelope' },
        { key: 'SMS', icon: 'fa-sms' },
    ];

    export default {
        name: 'LanguageChoiceModal',
        data() {
            return {
                shownChoice: null,
                pendingLanguage: null,
            };
        },
        computed: {
            ...mapGetters({
                languageChoice: 'auth/languageChoice',
            }),
            isSwitch() {
                return this.shownChoice?.mode === LANGUAGE_CHOICE_MODE.SWITCH;
            },
            isSaving() {
                return this.pendingLanguage !== null;
            },
            title() {
                return this.$t(`${CHOICE_KEYS}.${this.isSwitch ? 'SWITCH_TITLE' : 'LOGIN_TITLE'}`);
            },
            description() {
                return this.$t(`${CHOICE_KEYS}.${this.isSwitch ? 'SWITCH_TEXT' : 'LOGIN_TEXT'}`);
            },
            footnote() {
                return this.$t(`${CHOICE_KEYS}.FOOTNOTE`);
            },
            appliesTo() {
                return APPLIES_TO.map(({ key, icon }) => ({
                    key,
                    icon,
                    label: this.$t(`${CHOICE_KEYS}.APPLIES_TO.${key}`),
                }));
            },
            options() {
                if (!this.shownChoice) {
                    return [];
                }

                return getLanguageChoiceOptions(this.shownChoice).map((option) => {
                    const { name, flag } = i18nService.languages.find((item) => item.lang === option.language);

                    return {
                        ...option,
                        name,
                        flag,
                        tag: this.$t(`${CHOICE_KEYS}.TAGS.${option.tag}`),
                        caption: this.$t(`${CHOICE_KEYS}.${option.caption}`, option.language),
                        description: this.$t(`${CHOICE_KEYS}.${option.description}`, option.language),
                    };
                });
            },
        },
        watch: {
            languageChoice(choice) {
                if (choice) {
                    this.open(choice);
                } else {
                    this.$refs.modal.hide();
                }
            },
        },
        mounted() {
            if (this.languageChoice) {
                this.open(this.languageChoice);
            }
            this.confirmLanguageSwitch();
        },
        methods: {
            ...mapActions({
                updateProfileLanguage: `auth/${UPDATE_PROFILE_LANGUAGE}`,
                clearLanguageChoice: `auth/${CLEAR_LANGUAGE_CHOICE}`,
            }),
            open(choice) {
                this.shownChoice = choice;
                this.pendingLanguage = null;
                this.$refs.modal.show();
            },
            async choose(option) {
                if (option.dismiss) {
                    this.clearLanguageChoice();
                    return;
                }

                this.pendingLanguage = option.language;
                if (option.saveToProfile) {
                    try {
                        await this.updateProfileLanguage(option.language);
                    } catch (error) {
                        this.pendingLanguage = null;
                        Message.error('PROFILE.SECTIONS.LANGUAGE.SAVE_FAILED');
                        return;
                    }
                }

                this.showLanguage(option);
            },
            showLanguage({ language, reload }) {
                try {
                    i18nService.setActiveLanguage(language);
                } catch (error) {
                    console.error('[language] switch failed', error?.message);
                }
                this.clearLanguageChoice();

                if (reload) {
                    i18nService.setLanguageNotice(language);
                    window.location.reload();
                    return;
                }

                Message.success(`${CHOICE_KEYS}.NOTICE`);
            },
            onHidden() {
                this.shownChoice = null;
                this.pendingLanguage = null;
                if (this.languageChoice) {
                    this.clearLanguageChoice();
                }
            },
            confirmLanguageSwitch() {
                if (i18nService.takeLanguageNotice() === i18nService.getActiveLanguage()) {
                    Message.success(`${CHOICE_KEYS}.NOTICE`);
                }
            },
        },
    };
</script>
